import { useCallback } from 'react';
import { isEmpty } from 'lodash';
import { UseQueryResult } from '@tanstack/react-query';

import {
  HoldingAvailability,
  HoldingsAvailabilityDto,
  RecordAvailabilityGeneralStatus,
  RecordAvailabilityIconColor,
} from '@app/components';
import { useFeatureFlag } from '@app/hooks';
import { Feature } from '@app/constants';

interface UseProcessedAvailabilityProps {
  availabilityQuery: UseQueryResult;
  ids: string[];
}

export interface RtacDTO {
  holdings: HoldingsAvailabilityDto[];
}

export const useProcessedAvailability = ({
  availabilityQuery,
  ids,
}: UseProcessedAvailabilityProps) => {
  const {
    config: { specialCollectionLocationCodes },
  } = useFeatureFlag({ name: Feature.AEON });

  const getSummaryAvailabilityStatus = useCallback(
    (holdings: HoldingAvailability[]) => {
      if (availabilityQuery.isError) {
        return {
          generalStatus: RecordAvailabilityGeneralStatus.error,
          customDisplayStatus: RecordAvailabilityGeneralStatus.error,
          customIcon: RecordAvailabilityIconColor.error,
        };
      }

      return !isEmpty(holdings)
        ? {
            generalStatus: RecordAvailabilityGeneralStatus.available,
            customDisplayStatus: RecordAvailabilityGeneralStatus.available,
            customIcon: RecordAvailabilityIconColor.green,
          }
        : {
            generalStatus: RecordAvailabilityGeneralStatus.notAvailable,
            customDisplayStatus: RecordAvailabilityGeneralStatus.notAvailable,
            customIcon: RecordAvailabilityIconColor.red,
          };
    },
    [availabilityQuery.isError]
  );

  const checkIsInSpecialLocation = useCallback(
    ({ location }: { location: { code?: string } }) =>
      location.code && specialCollectionLocationCodes?.includes(location.code),
    [specialCollectionLocationCodes]
  );

  const getInstanceSummary = useCallback(
    ({ holdings, id }: { holdings: HoldingsAvailabilityDto[]; id: string }) => {
      const currentInstanceAvailability = holdings.find(
        holding => holding?.instanceId === id
      );
      const holdingsAvailabilityData: HoldingAvailability[] =
        currentInstanceAvailability?.holdings ?? [];

      const availableHoldings = holdingsAvailabilityData.filter(
        holding =>
          holding.status?.generalStatus ===
          RecordAvailabilityGeneralStatus.available
      );

      return {
        availabilityData: holdingsAvailabilityData,
        summaryAvailability: {
          status: getSummaryAvailabilityStatus(availableHoldings),
          isRequestable: currentInstanceAvailability?.requestable,
          isRTACApplicable: currentInstanceAvailability?.isRTACApplicable,
          hasItems: currentInstanceAvailability?.hasItems,
          hasHoldings: !isEmpty(currentInstanceAvailability?.holdings),
          hasVolumes: holdingsAvailabilityData.some(holdingAvailability =>
            Boolean(holdingAvailability.volume)
          ),
          isInSpecialCollection: holdingsAvailabilityData.some(
            checkIsInSpecialLocation
          ),
          isEveryItemInSpecialLocation: holdingsAvailabilityData.every(
            checkIsInSpecialLocation
          ),
          ...(!availabilityQuery.isError && {
            copiesRemaining: {
              available: availableHoldings.length,
              total: holdingsAvailabilityData.length,
            },
          }),
        },
      };
    },
    [
      availabilityQuery.isError,
      checkIsInSpecialLocation,
      getSummaryAvailabilityStatus,
    ]
  );

  const processAvailability = useCallback(
    ({ holdings = [] }: RtacDTO = {} as RtacDTO) =>
      ids.reduce((newAvailability, id) => {
        const { summaryAvailability, availabilityData } = getInstanceSummary({
          holdings,
          id,
        });

        newAvailability[id] = { availabilityData, summaryAvailability };

        return newAvailability;
      }, {}),
    [getInstanceSummary, ids]
  );

  return { processAvailability };
};
